import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import useTsgTrigger from "../hooks/useTsgTrigger";
import useSkillGenieSession from "../hooks/useSkillGenieSession";
import TSGLanguageSelector from "../components/TSGLanguageSelector";
import TSGCommandPalette from "../components/TSGCommandPalette";
import { useTenant } from "../Context/TenantContext";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";
import { useSubscription } from "../Context/SubscriptionContext";
import FeatureGuard from "../components/access/FeatureGuard";
import API_BASE_URL from "../Config/config";
import axios from "axios";

const NotesPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 // Tenant / NextGen Builder mode bypasses the learner paywall — access is
 // already gated externally (NextGenPaidFeatureGuard for TSG Nexus).
 if (isTenantMode) {
   return (
     <div className="min-h-screen bg-[#F8FAFC] text-body px-4 py-6 sm:px-6 md:px-12 lg:px-32 font-sans transition-all duration-300 ease-in-out relative overflow-hidden">
         <NotesContent mode={mode} />
     </div>
   );
 }

 return (
  <FeatureGuard feature="TSG_NOTES">
    <div className="min-h-screen bg-[#F8FAFC] text-body px-4 py-6 sm:px-6 md:px-12 lg:px-32 font-sans transition-all duration-300 ease-in-out relative overflow-hidden">
        <NotesContent mode={mode} />
    </div>
  </FeatureGuard>
 );
};

// Handwriting look for the note paper. The line spacing MUST match the
// textarea's line-height so the writing sits exactly on the ruled lines.
const LINE_HEIGHT = 36; // px
const HAND_FONT = "'Segoe Print','Bradley Hand','Comic Sans MS','Comic Sans',cursive";

const paperBackground = {
  backgroundColor: "#FFFDF5",
  backgroundImage: `repeating-linear-gradient(transparent, transparent ${LINE_HEIGHT - 1}px, #cfe0f4 ${LINE_HEIGHT - 1}px, #cfe0f4 ${LINE_HEIGHT}px)`,
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const NotesContent = ({ mode = "learner" }) => {
  const isTenantMode = mode === "tenant";
  const textareaRef = useRef(null);
  // True when the current draft was pre-filled from the floating-panel
  // scratchpad (localStorage 'user_notes'); cleared once that draft is saved
  // so the same text doesn't reappear as a "new" note forever.
  const seededFromScratchpad = useRef(false);
  const [showPalette, setShowPalette] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [savedNotes, setSavedNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [activeNoteId, setActiveNoteId] = useState(null); // null = new note
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const { session, updateSession } = useSkillGenieSession();
  const { subscription } = useSubscription();
  const navigate = useNavigate();

  const storedUser = localStorage.getItem("user");
  let userId = null;
  try {
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    // NextGen Builder accounts (nextgen_builders table) store their OWN
    // small integer id under `.id` — a different id space that can collide
    // with real learner user_ids. Use the namespaced pseudo-identity
    // instead (see NextGenAuthContext.jsx).
    userId = parsedUser?.accountType === 'nextgen_builder'
      ? localStorage.getItem('user_id')
      : (parsedUser?.userId || parsedUser?.id || parsedUser?.user_id);
  } catch (err) {
    console.error("❌ Failed to parse user:", err);
  }

  const fetchNotes = useCallback(async () => {
    if (!userId) { setIsLoadingNotes(false); return []; }
    try {
      const res = await axios.get(`${API_BASE_URL}/notes/${userId}`, {
        headers: { "user-id": userId },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setSavedNotes(list);
      return list;
    } catch (err) {
      console.error("❌ Failed to load notes:", err);
      return [];
    } finally {
      setIsLoadingNotes(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Hand off the floating-panel scratchpad: when the user clicks
  // "Full Screen" the quick note they were typing should be waiting here.
  useEffect(() => {
    const scratch = localStorage.getItem("user_notes");
    if (scratch && scratch.trim()) {
      setContent(scratch);
      seededFromScratchpad.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNewNote = () => {
    setActiveNoteId(null);
    setTitle("");
    setContent("");
    seededFromScratchpad.current = false;
    setSaveStatus("");
    textareaRef.current?.focus();
  };

  const openNote = (note) => {
    setActiveNoteId(note.id);
    setTitle(note.title || "");
    setContent(note.content || "");
    seededFromScratchpad.current = false;
    setSaveStatus("");
  };

  // IMPORTANT: the notes API is a FULL SYNC — the backend deletes every
  // note whose id is missing from the posted list. Always send the complete
  // merged list, never a single note, or every other saved note is wiped.
  const syncNotes = async (fullList) => {
    await axios.post(
      `${API_BASE_URL}/notes/${userId}`,
      { notes: fullList.map((n) => ({ id: n.id, title: n.title, content: n.content })) },
      { headers: { "user-id": userId } }
    );
  };

  const handleSave = async () => {
    if (!userId) {
      alert("Please log in to save notes.");
      return;
    }
    if (!content || !content.trim()) {
      alert("Note content cannot be empty.");
      return;
    }

    setIsSaving(true);
    setSaveStatus("Saving...");

    try {
      const finalTitle = (title && title.trim())
        ? title.trim()
        : content.trim().substring(0, 30) + (content.trim().length > 30 ? "..." : "");

      let merged;
      let savedId = activeNoteId;
      if (activeNoteId) {
        merged = savedNotes.map((n) =>
          n.id === activeNoteId ? { ...n, title: finalTitle, content } : n
        );
      } else {
        savedId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : String(Date.now());
        merged = [{ id: savedId, title: finalTitle, content }, ...savedNotes];
      }

      await syncNotes(merged);

      // The scratchpad draft now lives in the notebook — clear the handoff
      // so it doesn't come back as a duplicate next visit.
      if (seededFromScratchpad.current) {
        localStorage.removeItem("user_notes");
        seededFromScratchpad.current = false;
      }

      setActiveNoteId(savedId);
      setTitle(finalTitle);
      await fetchNotes();
      setSaveStatus("✅ Saved!");
      setTimeout(() => setSaveStatus(""), 3000);
    } catch (err) {
      console.error("❌ Failed to save note:", err);
      setSaveStatus("❌ Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (note, e) => {
    e.stopPropagation();
    if (!userId) return;
    if (!window.confirm(`Delete "${note.title || "this note"}"?`)) return;
    try {
      const remaining = savedNotes.filter((n) => n.id !== note.id);
      await syncNotes(remaining);
      setSavedNotes(remaining);
      if (activeNoteId === note.id) startNewNote();
    } catch (err) {
      console.error("❌ Failed to delete note:", err);
      alert("Failed to delete note.");
    }
  };

  useTsgTrigger(textareaRef, () => {
    console.log("🔥 @tsg triggered");
    setShowPalette(true);
  });

  const handleCommandSelect = (cmd) => {
    setShowPalette(false);
    // NextGen Builder / TSG Nexus must stay inside /nextgen-builder/ai-tutor/*
    // — routing to the plain /tsg/:intent page would eject the builder into
    // the learner flow (no NextGenLayout, no NextGenPaidFeatureGuard).
    navigate(isTenantMode ? `/nextgen-builder/ai-tutor/${cmd.intent}` : `/tsg/${cmd.intent}`, {
      state: {
        fromNotes: true,
        promptIntent: cmd.intent,
        language: session?.language || "en",
      },
    });
  };

  return (
    <>
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -ml-48 -mb-48"></div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 pb-8 mb-10 relative z-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate(-1)}
              className="w-12 h-12 bg-white flex items-center justify-center rounded-2xl shadow-sm border border-gray-100 hover:bg-purple-50 hover:border-[#5B21B6] transition-all group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-purple-200 p-3 mr-2">
                <svg className="w-full h-full text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-black text-[#111827] tracking-tight">
                  Smart Notes
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] font-black text-[#5B21B6] uppercase tracking-[0.2em]">Your Personal Notebook</span>
                  {subscription.isPaid && subscription.daysRemaining !== null && (
                    <div className="flex items-center gap-1.5 bg-green-50 px-3 py-1 rounded-full border border-green-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-green-600 text-[9px] font-black uppercase tracking-tight">
                        {subscription.daysRemaining} days left
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Language selector + New + Save */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl border border-gray-100 shadow-sm transition-all focus-within:ring-4 focus-within:ring-purple-50">
              <span className="text-lg">🌐</span>
              <TSGLanguageSelector
                selectedLanguage={session.language}
                onChange={(lang) => updateSession({ language: lang })}
                className="bg-transparent text-[#111827] text-[10px] font-black uppercase tracking-widest focus:outline-none cursor-pointer"
              />
            </div>

            <button
              className="bg-white text-[#5B21B6] px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest border border-purple-100 shadow-sm hover:bg-purple-50 hover:-translate-y-1 transition-all"
              onClick={startNewNote}
            >
              + New Note
            </button>

            <button
              className="bg-[#5B21B6] text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-[0_15px_30px_rgba(91,33,182,0.2)] hover:bg-[#7C3AED] hover:-translate-y-1 transition-all flex items-center gap-2 disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Note"}
            </button>
            {saveStatus && (
              <span className={`text-[10px] font-bold ${saveStatus.includes("✅") ? "text-green-600" : "text-red-600"}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>

        {/* Notebook layout: saved notes on the left, hand-note paper on the right */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 items-start">

          {/* Saved notes list */}
          <div className="bg-white rounded-[28px] border border-gray-100 shadow-[0_20px_50px_rgba(91,33,182,0.08)] overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <span className="text-[10px] font-black text-[#5B21B6] uppercase tracking-[0.2em]">My Notebook</span>
              <span className="text-[10px] font-black text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                {savedNotes.length} {savedNotes.length === 1 ? "note" : "notes"}
              </span>
            </div>

            <div className="max-h-[560px] overflow-y-auto custom-scrollbar divide-y divide-gray-50">
              {isLoadingNotes ? (
                <div className="p-8 text-center text-gray-400 text-xs font-bold uppercase tracking-widest">
                  Loading notes...
                </div>
              ) : savedNotes.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="text-3xl mb-3">📝</div>
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest leading-relaxed">
                    No saved notes yet.<br />Write on the paper and hit Save!
                  </p>
                </div>
              ) : (
                savedNotes.map((note) => (
                  <div
                    key={note.id}
                    onClick={() => openNote(note)}
                    className={`px-6 py-4 cursor-pointer transition-all group relative ${
                      activeNoteId === note.id
                        ? "bg-purple-50 border-l-4 border-[#5B21B6]"
                        : "hover:bg-gray-50 border-l-4 border-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-[#111827] truncate">
                          {note.title || "Untitled note"}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-relaxed" style={{ fontFamily: HAND_FONT }}>
                          {note.content}
                        </p>
                        <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest mt-2 inline-block">
                          {formatDate(note.updated_at || note.created_at)}
                        </span>
                      </div>
                      <button
                        onClick={(e) => handleDelete(note, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-rose-500 p-1.5 rounded-lg hover:bg-rose-50 flex-shrink-0"
                        title="Delete note"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Hand-note paper editor */}
          <div className="relative rounded-[28px] shadow-[0_40px_80px_rgba(91,33,182,0.12)] overflow-hidden border border-amber-100/60 focus-within:ring-[10px] focus-within:ring-purple-50 transition-all">
            {/* Spiral binding strip */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#5B21B6] via-[#7C3AED] to-[#A855F7] z-10"></div>
            {/* Red margin line, classic notebook style */}
            <div className="absolute top-0 bottom-0 left-14 w-px bg-rose-300/70 z-10 pointer-events-none hidden sm:block"></div>

            <div style={paperBackground} className="pt-6">
              {/* Title line */}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title of your note..."
                className="w-full bg-transparent px-6 sm:pl-20 sm:pr-10 text-2xl font-bold text-[#3B2A6B] placeholder-gray-300 focus:outline-none"
                style={{ fontFamily: HAND_FONT, height: `${LINE_HEIGHT}px`, lineHeight: `${LINE_HEIGHT}px` }}
              />

              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Start writing your note... (type @tsg to summon AI help)"
                className="w-full h-[300px] sm:h-[480px] px-6 sm:pl-20 sm:pr-10 pb-8 bg-transparent text-[#1E293B] placeholder-gray-300 focus:outline-none resize-none custom-scrollbar"
                style={{
                  fontFamily: HAND_FONT,
                  fontSize: "19px",
                  lineHeight: `${LINE_HEIGHT}px`,
                }}
              />
            </div>

            {showPalette && (
              <TSGCommandPalette
                open={showPalette}
                onClose={() => setShowPalette(false)}
                onSelect={handleCommandSelect}
              />
            )}
          </div>
        </div>

        {/* Helper info */}
        <div className="mt-6 flex items-center justify-center gap-3 text-gray-400 text-[10px] font-black uppercase tracking-widest relative z-10">
          <span className="w-2 h-2 rounded-full bg-[#5B21B6] animate-ping"></span>
          Pro Tip: Type <code className="text-[#5B21B6] font-black px-2 py-0.5 bg-purple-50 rounded border border-purple-100 mx-1">@tsg</code> anywhere to summon AI help
        </div>
    </>
  );
}

export default NotesPage;
